import { z } from "zod";

// ---------------------------------------------------------------------------
// credit-card-payoff — the minimum-repayment "debt trap" calculator.
//
// This tool is INFORMATION about the user's OWN existing credit-card debt, in
// the ASIC MoneySmart "minimum repayment trap" tradition: a reducing-balance
// amortisation that shows how long a balance takes to clear and how much
// interest it costs at the user's own rate — with the dramatic contrast between
// paying the regulated minimum-only and paying a fixed amount.
//
// REGULATORY (binding — see panels/credit-card-payoff.md, both CRITICALs).
// A credit card IS a credit product under the NCCP Act / National Credit Code —
// NOT an s764A financial product, so the AFSL perimeter is off-point, but
// "credit assistance" (suggesting/assisting a consumer into a particular credit
// contract) is a LICENSED activity requiring an ACL. Routing a PERSONALISED
// payoff result to any paid credit-product handoff — balance-transfer card,
// personal loan, debt-consolidation loan — is the NCCP credit-assistance
// kill-line, with no carve-out, on a financially-stressed cohort. So:
//  - Information, not advice. The trap is presented as ARITHMETIC ("paying the
//    minimum, this balance takes N years and $X interest"), never "consolidate",
//    "get a balance transfer", "refinance", or "you should".
//  - NO handoff to any credit product from a personalised result. Encoded as a
//    hard guard (`assertNoCreditHandoff` in calculate.ts) that runs over every
//    output and throws if a credit-product / debt-consolidation / advice term
//    leaks in. Tested in calculate.test.ts. Affiliate is out of scope for v1.
//  - Outputs are conditional and sourced ("at these inputs, paying $Y clears it
//    M months sooner"), never an unconditional "save $X" headline.
//  - Cite ASIC MoneySmart (the minimum-repayment mechanic + the government
//    baseline calculator).
//
// VERSIONING — DATE-KEYED, not FY-keyed. There is no annual tax table here. The
// only "data" is reference/benchmark constants (the RBA F5 average standard /
// low-rate card APRs, the typical minimum-repayment rule) used for DEFAULTS and
// CONTEXT — they reprice but do NOT gate the maths (rate + minimum rule are user
// inputs). They are stored as dated constants in `rates-YYYY-MM-DD.ts`, sourced
// to the RBA F5 series BY ID (not column position) and ASIC MoneySmart, and a
// new indexation date gets a new file rather than mutating a shipped one.
// ---------------------------------------------------------------------------

/** Provenance + audit metadata for a date-keyed benchmark/defaults set. */
export interface CardPayoffRateSetMeta {
  /** Inclusive ISO start date these benchmark defaults take effect. */
  effectiveFrom: string;
  /**
   * Inclusive ISO end date, or `null` for the open-ended current period (until
   * a refresh supersedes it).
   */
  effectiveTo: string | null;
  /** Human label, e.g. "Card payoff benchmarks effective 5 June 2026". */
  label: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
}

/**
 * The regulated minimum-repayment rule (ASIC MoneySmart, figure #5, CITED):
 * "Minimum repayments are typically 2 or 2.5%, or a set dollar amount (usually
 * around $20), whichever is greater. Your minimum repayment will never be more
 * than your closing balance." This is the mechanic that drives the
 * shrinking-minimum loop — the minimum recomputes off the closing balance each
 * month until it hits the dollar floor, dragging payoff out for decades.
 */
export interface MinimumRepaymentRule {
  /** Percentage of the closing balance (fraction, e.g. 0.02 = 2%). */
  percentOfBalance: number;
  /** Dollar floor — the minimum is the greater of $floor or %×balance. */
  dollarFloor: number;
}

/** A benchmark/defaults set for one period (date-keyed). */
export interface CardPayoffRateSet extends CardPayoffRateSetMeta {
  /**
   * RBA F5 average ADVERTISED STANDARD credit-card rate (annual, fraction).
   * Series FILRPLRCCS. A DEFAULT/context value only — the user's own rate is the
   * input that gates the maths. Figure #1, CITED.
   */
  standardRate: number;
  /**
   * RBA F5 average LOW-RATE credit-card rate (annual, fraction). Series
   * FILRPLRCCL. Default/context only. Figure #2, CITED.
   */
  lowRate: number;
  /**
   * The typical regulated minimum-repayment rule (ASIC MoneySmart). Default rule
   * used when the user picks the "minimum" strategy without overriding it.
   * Figure #5, CITED.
   */
  typicalMinimum: MinimumRepaymentRule;
}

// --- Repayment strategy -------------------------------------------------------

/**
 * How the monthly repayment is determined.
 *  - `fixed` — the same dollar amount every month (the user commits to $X/mo).
 *  - `minimum` — the regulated minimum: the greater of a dollar floor or a
 *    percent of the CLOSING balance, recomputed each month (the shrinking
 *    minimum that drives the trap), capped at the closing balance.
 */
export const RepaymentStrategySchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("fixed"),
    /** Fixed repayment per month, in dollars. */
    monthlyPayment: z.number().min(0),
  }),
  z.object({
    kind: z.literal("minimum"),
    /** Percentage of closing balance (fraction). Defaults to the benchmark 2%. */
    percentOfBalance: z.number().min(0).max(1).optional(),
    /** Dollar floor. Defaults to the benchmark $20. */
    dollarFloor: z.number().min(0).optional(),
  }),
]);

export type RepaymentStrategy = z.infer<typeof RepaymentStrategySchema>;

// --- payoffSchedule -----------------------------------------------------------

export const PayoffInputSchema = z.object({
  /** Current outstanding balance, in dollars. */
  balance: z.number().min(0),
  /** Annual purchase interest rate (APR) as a fraction, e.g. 0.2099 = 20.99%. */
  annualRate: z.number().min(0),
  /** The repayment strategy: fixed $/month, or the regulated minimum. */
  strategy: RepaymentStrategySchema,
  /**
   * ISO start date (YYYY-MM-DD) the schedule runs from — passed in, never
   * derived from the clock, so the payoff date is deterministic. The first
   * payment lands one month after this date.
   */
  startDate: z.string(),
  /**
   * Safety cap on the number of months to amortise. When a payment never
   * clears the balance (payment ≤ first month's interest) the loop would run
   * forever; we stop at this many months and flag `clears: false`. Default 1200
   * (100 years) — well beyond any realistic real-world payoff.
   */
  maxMonths: z.number().int().min(1).default(1200),
});

export type PayoffInput = z.infer<typeof PayoffInputSchema>;
/** Pre-parse shape (defaulted fields optional) — accepted at the call boundary. */
export type PayoffRawInput = z.input<typeof PayoffInputSchema>;

export interface PayoffOutput {
  /** Balance the schedule ran against. */
  balance: number;
  /** Annual rate (fraction) the schedule ran against. */
  annualRate: number;
  /**
   * Whether the balance clears within `maxMonths`. FALSE when the repayment is
   * at or below the monthly interest, so the balance never reduces (degenerate
   * input — handled, never an infinite loop).
   */
  clears: boolean;
  /**
   * Whole months to clear the balance. `null` when `clears` is false (it never
   * pays off under this strategy).
   */
  monthsToClear: number | null;
  /** Total interest paid over the life of the schedule, in dollars (rounded). */
  totalInterestPaid: number;
  /** Total of all repayments made (principal + interest), in dollars. */
  totalPaid: number;
  /**
   * ISO payoff date (YYYY-MM-DD): `startDate` advanced by `monthsToClear`
   * months. `null` when it never clears.
   */
  payoffDate: string | null;
  /**
   * The FIRST month's interest charge, in dollars. Exposed because the
   * "never clears" condition is exactly "first payment ≤ this".
   */
  firstMonthInterest: number;
}

// --- minimumTrap --------------------------------------------------------------

export const MinimumTrapInputSchema = z.object({
  /** Current outstanding balance, in dollars. */
  balance: z.number().min(0),
  /** Annual purchase rate (APR) as a fraction. */
  annualRate: z.number().min(0),
  /**
   * The fixed monthly amount to contrast the minimum-only path against, in
   * dollars. This is the "what if I committed to $X/month" comparison.
   */
  fixedMonthlyPayment: z.number().min(0),
  /** The minimum-repayment rule. Defaults to the benchmark 2% / $20. */
  minimumRule: z
    .object({
      percentOfBalance: z.number().min(0).max(1),
      dollarFloor: z.number().min(0),
    })
    .optional(),
  /** ISO start date (YYYY-MM-DD). */
  startDate: z.string(),
  /** Safety cap, passed through to both schedules. */
  maxMonths: z.number().int().min(1).default(1200),
});

export type MinimumTrapInput = z.infer<typeof MinimumTrapInputSchema>;
/** Pre-parse shape (defaulted fields optional) — accepted at the call boundary. */
export type MinimumTrapRawInput = z.input<typeof MinimumTrapInputSchema>;

export interface MinimumTrapOutput {
  /** Balance both paths ran against. */
  balance: number;
  /** Annual rate both paths ran against. */
  annualRate: number;
  /** The minimum-only amortisation result. */
  minimumOnly: PayoffOutput;
  /** The fixed-payment amortisation result. */
  fixed: PayoffOutput;
  /**
   * EXTRA interest the minimum-only path costs over the fixed path, in dollars
   * (minimumOnly.totalInterestPaid − fixed.totalInterestPaid). Presented as
   * cited arithmetic, never as a "save $X" claim. `null` if the minimum path
   * never clears (then the extra is unbounded — the copy says so qualitatively).
   */
  extraInterest: number | null;
  /**
   * EXTRA months the minimum-only path takes over the fixed path. `null` if the
   * minimum path never clears.
   */
  extraMonths: number | null;
  /** Whether total interest on the minimum-only path EXCEEDS the principal. */
  minimumInterestExceedsPrincipal: boolean;
}
